import {
  useMutation, useQuery, useQueryClient, UseQueryOptions,
} from 'react-query';
import { MaestroAPI, maestroApiCall } from '@michelin/central-provider';
import { baseURL } from 'Types/APITypes';
import {
  DealerProfileLocation,
  LocationBT,
  LocationShort,
  QueryKeys,
} from 'Types';

interface saveParams {
  stNumber: string,
  data: DealerProfileLocation
}

function validateLocatioNumber(number:string) {
  // needs to be 7 digits
  return /^\d{7}$/.test(number);
}

async function getBTLocation(btNumber: string) {
  if (!btNumber) return null;
  if (!validateLocatioNumber(btNumber)) return null;
  return maestroApiCall({ method: 'GET', endpoint: `locations/${btNumber}/bt-location`, baseURL });
}

const getSTLocation = (stNumber: string) => maestroApiCall({ method: 'GET', endpoint: `locations/${stNumber}/retail-location`, baseURL }).then((data) => {
  // eslint-disable-next-line no-param-reassign
  data.google_places = {
    google_places_id: data.google_places_id,
    geo_code_latitude: data.geo_code_latitude,
    geo_code_longitude: data.geo_code_longitude,
    requested_geo_code_explanation: data.requested_geo_code_explanation,
    requested_geo_code_latitude: data.requested_geo_code_latitude,
    requested_geo_code_longitude: data.requested_geo_code_longitude,
    requested_geo_code_update: data.requested_geo_code_update,
  };
  return data;
});

async function getShortLocation(providerId: string) {
  if (!providerId) return null;
  if (!validateLocatioNumber(providerId)) return null;
  return maestroApiCall({ method: 'GET', endpoint: `/locations/${providerId}/location-information`, baseURL });
}

export function useLocationST(stNumber: string, options?:UseQueryOptions) {
  return useQuery<unknown, unknown, any>([QueryKeys.stLocation, stNumber], () => getSTLocation(stNumber), options);
}

export function useOptionalLocationST(stNumber: string|undefined) {
  return useQuery<DealerProfileLocation|null>([QueryKeys.stLocation, stNumber], () => (stNumber ? getSTLocation(stNumber) : null));
}

export function useLocationBT(btNumber: string) {
  return useQuery<LocationBT>([QueryKeys.btLocation, btNumber], () => getBTLocation(btNumber));
}

export function useLocationShort(providerId: string) {
  return useQuery<LocationShort>([QueryKeys.shortLocation, providerId], () => getShortLocation(providerId));
}

async function saveLocation({ stNumber, data }: saveParams) {
  const apiType: MaestroAPI = { method: 'PUT', endpoint: `locations/${stNumber}/retail-location`, baseURL };
  const newData: DealerProfileLocation = { ...data };

  // Remove the extra data in the entity.
  delete newData.google_places;

  const res = await maestroApiCall(apiType, newData);
  return res;
}

export function useSaveRetailLocation(stNumber: string) {
  const queryClient = useQueryClient();
  return useMutation(saveLocation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.stLocation, stNumber]);
    },
  });
}
